import "./HeroWithImageThematic.scss";
import { useState } from "react";
import {
  GridPresetOneHalf,
  GridRow,
  InfoSmallIcon,
  Picture,
  Labels,
  VideoWithPoster,
  HideFor,
  OptimizelyEditProperty,
} from "/App.Components";
import { HeroWithImageThematicProps } from "/Models/Generated/HeroWithImageThematicProps";
import { createSpreadableBem } from "/Helpers/createBem";
import { DesignTheme } from "/App.GeneratedEnums";

interface HeroWithImageThematicExtendedProps
  extends HeroWithImageThematicProps {
  theme?: DesignTheme;
}

const HeroWithImageThematic = ({
  labels,
  heading,
  headingPropertyName,
  leadText,
  leadTextPropertyName,
  image,
  video,
  useH2InHeader,
  theme = DesignTheme.Standard,
}: HeroWithImageThematicExtendedProps) => {
  const [posterClicked, setPosterClicked] = useState(false);
  const bem = createSpreadableBem("hero-with-image-thematic-norad");

  return (
    <div {...bem("", { "--insight": theme === DesignTheme.Insight })}>
      <div
        {...bem("__content", {
          "--no-media": !image && !video,
        })}
      >
        <GridRow>
          {theme === DesignTheme.Standard && (
            <HeroMedia
              image={image}
              video={video}
              posterClicked={posterClicked}
              setPosterClicked={() => setPosterClicked((prev) => !prev)}
              bem={bem}
              theme={theme}
            />
          )}

          <GridPresetOneHalf mobileOrder={-1}>
            <div {...bem("__text-and-labels-container")}>
              {labels && (
                <div {...bem("__tags")}>
                  <Labels {...labels} />
                </div>
              )}
              <div
                {...bem("__text-container", {
                  "--centered": !!labels && (!!image || !!video),
                })}
              >
                <OptimizelyEditProperty propertyName={headingPropertyName}>
                  {heading && !useH2InHeader && (
                    <h1
                      {...bem("__heading", {
                        "--no-media": !video && !image,
                        "--insight": theme === DesignTheme.Insight,
                      })}
                    >
                      {heading}
                    </h1>
                  )}
                  {heading && useH2InHeader && (
                    <h2
                      {...bem("__heading", {
                        "--no-media": !video && !image,
                        "--insight": theme === DesignTheme.Insight,
                      })}
                    >
                      {heading}
                    </h2>
                  )}
                </OptimizelyEditProperty>
                <OptimizelyEditProperty propertyName={leadTextPropertyName}>
                  {leadText && (
                    <p
                      {...bem("__lead-text", {
                        "--insight": theme === DesignTheme.Insight,
                      })}
                    >
                      {leadText}
                    </p>
                  )}
                </OptimizelyEditProperty>
              </div>
            </div>
          </GridPresetOneHalf>
          {theme === DesignTheme.Insight && (
            <HeroMedia
              image={image}
              video={video}
              posterClicked={posterClicked}
              setPosterClicked={() => setPosterClicked((prev) => !prev)}
              bem={bem}
              theme={theme}
            />
          )}
        </GridRow>
      </div>
    </div>
  );
};

export default HeroWithImageThematic;

interface HeroImageProps {
  image?: HeroWithImageThematicProps["image"];
  video?: HeroWithImageThematicProps["video"];
  posterClicked?: boolean;
  setPosterClicked?: () => void;
  bem: ReturnType<typeof createSpreadableBem>;
  theme: DesignTheme;
}

const HeroMedia = ({
  image,
  video,
  posterClicked,
  setPosterClicked,
  bem,
  theme,
}: HeroImageProps) => {
  if (video)
    return (
      <HeroVideo
        video={video}
        posterClicked={posterClicked}
        setPosterClicked={setPosterClicked}
        bem={bem}
        theme={theme}
      />
    );

  if (!image) return null;

  return (
    <GridPresetOneHalf>
      <div {...bem("__thumbnail")}>
        <Picture
          {...image}
          pictureAspectRatio="16/9"
          pictureBehaviour="Cover"
          eagerLoading
        />
        {(!!image?.imageText || !!image.copyright) && (
          <div {...bem("__copyright")}>
            {!!image?.copyright && (
              <>
                <HideFor mobile tablet>
                  <InfoSmallIcon
                    text={`${image?.copyrightLabel ?? ""} ${image.copyright}`}
                    theme={
                      theme === DesignTheme.Insight
                        ? "insightPurple"
                        : "standard"
                    }
                  />
                </HideFor>
                <HideFor desktop desktopMedium desktopLargeUp>
                  <InfoSmallIcon
                    text={`${image?.copyrightLabel ?? ""} ${image.copyright}`}
                    theme={
                      theme === DesignTheme.Insight
                        ? "insightPurple"
                        : "standard"
                    }
                  />
                </HideFor>
              </>
            )}
          </div>
        )}
      </div>
    </GridPresetOneHalf>
  );
};

interface HeroVideoProps {
  video?: HeroWithImageThematicProps["video"];
  posterClicked?: boolean;
  setPosterClicked?: () => void;
  bem: ReturnType<typeof createSpreadableBem>;
  theme: DesignTheme;
}

const HeroVideo = ({
  video,
  posterClicked,
  setPosterClicked,
  bem,
  theme,
}: HeroVideoProps) => {
  if (!(video && posterClicked !== undefined && setPosterClicked)) return null;

  return (
    <GridPresetOneHalf>
      <div {...bem("__thumbnail")}>
        <div {...bem("__video")}>
          <VideoWithPoster
            posterClicked={posterClicked}
            setPosterClicked={setPosterClicked}
            {...video}
            posterBehaviour="Cover"
            posterAspectRatio="16/9"
            eagerLoading
          />
        </div>

        {(!!video?.copyright || !!video?.posterImage?.imageText) && (
          <div {...bem("__copyright")}>
            {!!video?.copyright && posterClicked && (
              <>
                <HideFor mobile tablet>
                  <InfoSmallIcon
                    text={`${video?.copyrightLabel ?? ""} ${video.copyright}`}
                    theme={
                      theme === DesignTheme.Insight
                        ? "insightPurple"
                        : "standard"
                    }
                  />
                </HideFor>
                <HideFor desktop desktopMedium desktopLargeUp>
                  <InfoSmallIcon
                    text={`${video?.copyrightLabel ?? ""} ${video.copyright}`}
                    theme={
                      theme === DesignTheme.Insight
                        ? "insightPurple"
                        : "standard"
                    }
                  />
                </HideFor>
              </>
            )}
            {!!video?.posterImage?.copyright && !posterClicked && (
              <>
                <HideFor mobile tablet>
                  <InfoSmallIcon
                    text={`${
                      video?.posterImage?.copyrightLabel ?? ""
                    } ${video?.posterImage?.copyright}`}
                    theme={
                      theme === DesignTheme.Insight
                        ? "insightPurple"
                        : "standard"
                    }
                  />
                </HideFor>
                <HideFor desktop desktopMedium desktopLargeUp>
                  <InfoSmallIcon
                    text={`${
                      video?.posterImage?.copyrightLabel ?? ""
                    } ${video?.posterImage?.copyright}`}
                    theme={
                      theme === DesignTheme.Insight
                        ? "insightPurple"
                        : "standard"
                    }
                  />
                </HideFor>
              </>
            )}
          </div>
        )}
      </div>
    </GridPresetOneHalf>
  );
};
